import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import { useReports, downloadReport } from '../../apis/reports';
import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import Button from '@mui/material/Button';

const headCells = [
  { id: 'tipo', label: 'Tipo' },
  { id: 'filtros', label: 'Filtros' },
  { id: 'dataCriacao', label: 'Data Geração' },
  { id: 'usuario', label: 'Gerado por' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Ações' }
];

function EnhancedTableHead(props) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'left'}
            padding={'normal'}
            sx={{ fontSize: '12px', fontWeight: 'bold' }}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  rowCount: PropTypes.number.isRequired,
};

const ReportsTable = props => {
  const { setLoadingData } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [totalElements, setTotalElements] = React.useState(0);
  const [reportsList, setReportsList] = React.useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError, error, isFetching, refetch, fetchNextPage } = useReports(rowsPerPage);

  React.useEffect(() => {
    const loadingStatus = isLoading || isFetching;
    setLoadingData(loadingStatus);

    if (!data) return;

    const allReports = data.pages.flatMap(page => page.content || []);

    setTotalElements(data.pages[0]?.totalElements || 0);
    setReportsList(allReports);

    if (isError) {
      console.error("Erro ao carregar dados:", error);
    }
  }, [data, isLoading, isError, error, isFetching, setLoadingData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchNextPage();
  };

  const handleDownload = async (id, status) => {
    if (status !== 'CONCLUIDO') {
      enqueueSnackbar('Relatório ainda não está pronto para download', { variant: 'warning' });
      return;
    }
    try {
      setLoadingData(true);
      const response = await downloadReport(id);
      window.open(response.url, "_blank");
    } catch (e) {
      enqueueSnackbar('Erro ao baixar relatório', { variant: 'error' });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    refetch();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PROCESSANDO': return 'warning';
      case 'CONCLUIDO': return 'success';
      case 'FALHA': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PROCESSANDO': return 'Processando';
      case 'CONCLUIDO': return 'Concluído';
      case 'FALHA': return 'Falha';
      default: return status;
    }
  };

  const renderFiltros = (filtrosStr) => {
    try {
      if (!filtrosStr) return '-';
      const filtros = JSON.parse(filtrosStr);
      
      const labels = {
        bancoNome: 'Banco',
        bancoId: 'Banco',
        dataContratoInicio: 'Data Contrato Início',
        dataContratoFim: 'Data Contrato Fim',
        dataVendaInicio: 'Data Venda Início',
        dataVendaFim: 'Data Venda Fim'
      };

      // Se bancoNome existe, não mostra o bancoId (UUID)
      const hiddenKeys = filtros.bancoNome ? ['bancoId'] : [];

      const formatValue = (key, value) => {
        if (key.startsWith('data') && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [y, m, d] = value.split('-');
          return `${d}/${m}/${y}`;
        }
        return value;
      };

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(filtros).map(([key, value]) => {
            if (!value || hiddenKeys.includes(key)) return null;
            const label = labels[key] || key;
            return (
              <Chip 
                key={key} 
                label={`${label}: ${formatValue(key, value)}`} 
                size="small" 
                variant="outlined" 
                sx={{ fontSize: '10px', backgroundColor: 'rgba(0,0,0,0.03)' }} 
              />
            );
          })}
        </Box>
      );
    } catch (e) {
      return filtrosStr;
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - reportsList.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Card variant="outlined" sx={{ borderRadius: '10px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
            <Button 
              onClick={() => refetch()} 
              startIcon={<RefreshIcon />} 
              variant="outlined" 
              size="small"
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px' }}
            >
              Atualizar
            </Button>
          </Box>
          <Grid container alignItems="center">
            <TableContainer>
              <Table sx={{ minWidth: 750 }} size='small'>
                <EnhancedTableHead rowCount={reportsList.length} />
                <TableBody>
                  {reportsList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <TableRow hover tabIndex={-1} key={row.id}>
                          <TableCell></TableCell>
                          <TableCell style={{ width: '10%', fontSize: '11px' }}>{row.tipo}</TableCell>
                          <TableCell style={{ width: '40%' }}>
                            {renderFiltros(row.filtrosJson)}
                          </TableCell>
                          <TableCell style={{ width: '15%', fontSize: '11px' }}>
                            {row.dataCriacao ? dayjs(row.dataCriacao).format('DD/MM/YYYY HH:mm') : ''}
                          </TableCell>
                          <TableCell style={{ width: '10%', fontSize: '11px' }}>{row.usuario?.nome || '-'}</TableCell>
                          <TableCell style={{ width: '10%' }}>
                            <Chip size="small" label={getStatusLabel(row.status)} color={getStatusColor(row.status)} sx={{ fontSize: '10px', fontWeight: 'bold' }} />
                          </TableCell>
                          <TableCell style={{ width: '10%' }}>
                            <IconButton onClick={() => handleDownload(row.id, row.status)} size='small' disabled={row.status !== 'CONCLUIDO'}>
                              <DownloadIcon fontSize='small' color={row.status === 'CONCLUIDO' ? 'primary' : 'disabled'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 33 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 75, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportsTable;
